import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw, Check, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { useLocalizedQuizQuestions, useLocalizedQuizIntro, useLocalizedBreed, scoreQuiz } from "@/i18n/hooks";

export default function Quiz() {
    const [stage, setStage] = useState("intro");
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [email, setEmail] = useState("");
    const [emailSaved, setEmailSaved] = useState(false);
    const { t } = useLang();

    const quizQuestions = useLocalizedQuizQuestions();
    const quizIntro = useLocalizedQuizIntro();
    const currentQ = quizQuestions[step];
    const progress = ((step + (stage === "result" ? 1 : 0)) / quizQuestions.length) * 100;

    const start = () => { setStage("quiz"); setStep(0); setAnswers({}); };
    const select = (optionId) => {
        const next = { ...answers, [currentQ.id]: optionId };
        setAnswers(next);
        setTimeout(() => {
            if (step + 1 < quizQuestions.length) setStep(step + 1);
            else setStage("result");
        }, 260);
    };
    const back = () => { if (step > 0) setStep(step - 1); };
    const restart = () => { setStage("intro"); setStep(0); setAnswers({}); setEmail(""); setEmailSaved(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

    const ranked = stage === "result" ? scoreQuiz(answers) : [];
    const topSlug = ranked[0]?.slug;
    const top = useLocalizedBreed(topSlug);
    const alt1 = useLocalizedBreed(ranked[1]?.slug);
    const alt2 = useLocalizedBreed(ranked[2]?.slug);
    const alts = [ranked[1] && { ...ranked[1], breed: alt1 }, ranked[2] && { ...ranked[2], breed: alt2 }].filter(Boolean);

    const saveEmail = (e) => {
        e.preventDefault();
        if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            toast.error(t("quiz.emailInvalid"));
            return;
        }
        try {
            const stored = JSON.parse(localStorage.getItem("omkar_quiz_leads") || "[]");
            stored.push({ email, top: top?.slug, at: new Date().toISOString() });
            localStorage.setItem("omkar_quiz_leads", JSON.stringify(stored));
        } catch (err) { console.warn("Could not save quiz lead locally", err); }
        setEmailSaved(true);
        toast.success(t("quiz.emailSuccessToast"));
    };

    return (
        <div className="bg-paper min-h-screen" data-testid="quiz-page">
            {stage !== "intro" && (
                <div className="fixed top-[64px] md:top-[72px] left-0 right-0 z-40 h-[2px] bg-transparent" data-testid="quiz-progress-bar">
                    <div className="h-full bg-gold transition-[width] duration-500 ease-editorial" style={{ width: `${progress}%` }} />
                </div>
            )}

            {stage === "intro" && (
                <section className="pt-40 md:pt-48 pb-32 container-editorial" data-testid="quiz-intro">
                    <SectionLabel label={t("quiz.label")} number="Q" className="mb-8" />
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end">
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight md:col-span-8">
                            {quizIntro.title}
                        </h1>
                        <p className="md:col-span-4 text-ink/70 text-lg leading-relaxed">
                            {quizIntro.subtitle}
                        </p>
                    </div>
                    <div className="mt-16 pt-12 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-10">
                        <Reveal>
                            <div>
                                <div className="font-serif text-5xl text-gold mb-4 kbd-num">05</div>
                                <div className="overlabel text-earth mb-2">{t("quiz.questionsLabel")}</div>
                                <p className="text-ink/70 leading-relaxed">{t("quiz.cardsQuestions")}</p>
                            </div>
                        </Reveal>
                        <Reveal delay={100}>
                            <div>
                                <div className="font-serif text-5xl text-gold mb-4 kbd-num">60s</div>
                                <div className="overlabel text-earth mb-2">{t("quiz.timeLabel")}</div>
                                <p className="text-ink/70 leading-relaxed">{t("quiz.cardsTime")}</p>
                            </div>
                        </Reveal>
                        <Reveal delay={200}>
                            <div>
                                <div className="font-serif text-5xl text-gold mb-4 kbd-num">06</div>
                                <div className="overlabel text-earth mb-2">{t("quiz.scoreLabel")}</div>
                                <p className="text-ink/70 leading-relaxed">{t("quiz.cardsScore")}</p>
                            </div>
                        </Reveal>
                    </div>
                    <div className="mt-16">
                        <button onClick={start} data-testid="quiz-start" className="group inline-flex items-center gap-3 bg-forest text-paper px-10 py-5 rounded-full text-sm tracking-wide font-medium hover:bg-gold hover:text-ink transition-colors duration-500">
                            {t("common.buttons.begin")}
                            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                </section>
            )}

            {stage === "quiz" && (
                <section className="pt-32 md:pt-40 pb-24 container-editorial min-h-screen" data-testid={`quiz-step-${step + 1}`}>
                    <div className="max-w-3xl">
                        <div className="flex items-baseline justify-between mb-10">
                            <div className="overlabel text-earth">
                                {t("common.labels.question")} <span className="text-forest">{String(step + 1).padStart(2, "0")}</span> / {String(quizQuestions.length).padStart(2, "0")}
                            </div>
                            {step > 0 && (
                                <button onClick={back} data-testid="quiz-back" className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-2">
                                    <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.buttons.previous")}
                                </button>
                            )}
                        </div>
                        <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight mb-4" key={currentQ.id}>
                            {currentQ.title}
                        </h2>
                        <p className="text-ink/60 text-lg leading-relaxed mb-12">{currentQ.subtitle}</p>

                        <div className="space-y-3" key={`opts-${currentQ.id}`}>
                            {currentQ.options.map((opt, i) => {
                                const chosen = answers[currentQ.id] === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        onClick={() => select(opt.id)}
                                        data-testid={`quiz-option-${currentQ.id}-${opt.id}`}
                                        className={`w-full text-left group relative overflow-hidden rounded-[12px] border transition-all duration-500 ease-editorial px-6 md:px-8 py-6 flex items-center justify-between gap-6 animate-fade-up ${
                                            chosen ? "border-forest bg-forest text-paper" : "border-border bg-white text-ink hover:border-ink hover:shadow-soft"
                                        }`}
                                        style={{ animationDelay: `${i * 60}ms` }}
                                    >
                                        <div>
                                            <div className="font-serif text-2xl md:text-3xl leading-tight">{opt.label}</div>
                                            <div className={`text-sm mt-1 ${chosen ? "text-paper/70" : "text-ink/55"}`}>{opt.desc}</div>
                                        </div>
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${chosen ? "border-paper bg-paper text-forest" : "border-border group-hover:border-ink"}`}>
                                            {chosen ? <Check size={16} strokeWidth={2} /> : <ArrowRight size={16} strokeWidth={1.5} />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {stage === "result" && top && (
                <section className="pt-32 md:pt-40 pb-24 container-editorial" data-testid="quiz-result">
                    <SectionLabel label={t("quiz.resultLabel")} number="✓" className="mb-6" />
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-end mb-12">
                        <div className="md:col-span-8">
                            <div className="overlabel text-earth mb-4 flex items-center gap-2">
                                <Sparkles size={14} strokeWidth={1.5} className="text-gold" /> {t("quiz.recommended")}
                            </div>
                            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tight mb-4">
                                {top.name}
                            </h1>
                            <p className="font-serif italic text-2xl text-ink/80 max-w-2xl">{top.tagline}</p>
                        </div>
                        <div className="md:col-span-4 text-right">
                            <div className="overlabel text-earth mb-2">{t("common.labels.matchScore")}</div>
                            <div className="font-serif text-7xl md:text-8xl text-gold kbd-num leading-none" data-testid="quiz-result-score">
                                {ranked[0].match}<span className="text-3xl">%</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start mb-20">
                        <div className="md:col-span-6 relative overflow-hidden rounded-[12px] aspect-[4/5] shadow-editorial">
                            <img src={top.heroImage} alt={top.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="md:col-span-6 space-y-8">
                            <div>
                                <div className="overlabel text-earth mb-3">{t("quiz.whySuits")}</div>
                                <p className="font-serif text-2xl md:text-3xl leading-snug">{top.recommendation.bestFor}</p>
                            </div>
                            <div className="border-t border-border pt-6 grid grid-cols-2 gap-6">
                                <div>
                                    <div className="overlabel text-earth text-[10px] mb-2">{t("quiz.avgYield")}</div>
                                    <div className="font-serif text-2xl kbd-num">{top.production.avgYield}</div>
                                </div>
                                <div>
                                    <div className="overlabel text-earth text-[10px] mb-2">{t("quiz.milkFat")}</div>
                                    <div className="font-serif text-2xl kbd-num">{top.production.milkFat}</div>
                                </div>
                                <div>
                                    <div className="overlabel text-earth text-[10px] mb-2">{t("quiz.monthlyOp")}</div>
                                    <div className="font-serif text-xl kbd-num">{top.recommendation.monthlyOpCost}</div>
                                </div>
                                <div>
                                    <div className="overlabel text-earth text-[10px] mb-2">{t("quiz.monthlyMilk")}</div>
                                    <div className="font-serif text-xl kbd-num">{top.recommendation.monthlyMilk}</div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-4 pt-4">
                                <Link to={`/breeds/${top.slug}`} data-testid="quiz-view-breed" className="group inline-flex items-center gap-3 bg-forest text-paper px-8 py-4 rounded-full text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors duration-500">
                                    {t("quiz.seeProfile")} {top.name} {t("quiz.profileSuffix")}
                                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                                <button onClick={restart} data-testid="quiz-restart" className="inline-flex items-center gap-3 border border-border text-ink px-8 py-4 rounded-full text-sm tracking-wide hover:border-ink transition-colors duration-500">
                                    <RotateCcw size={14} strokeWidth={1.5} /> {t("common.buttons.retake")}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-border pt-16 mb-20">
                        <SectionLabel label={t("quiz.alsoConsider")} className="mb-10" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {alts.map((a) => (
                                <Link key={a.slug} to={`/breeds/${a.slug}`} data-testid={`quiz-alt-${a.slug}`} className="group relative overflow-hidden rounded-[12px] bg-white shadow-soft hover:shadow-lift transition-shadow duration-500">
                                    <div className="grid grid-cols-5">
                                        <div className="col-span-2 aspect-[3/4] overflow-hidden">
                                            <img src={a.breed.image} alt={a.breed.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms] ease-editorial" />
                                        </div>
                                        <div className="col-span-3 p-6 md:p-8 flex flex-col justify-center">
                                            <div className="overlabel text-earth mb-2 text-[10px]">{t("common.labels.match")} {a.match}%</div>
                                            <div className="font-serif text-3xl leading-tight mb-2">{a.breed.name}</div>
                                            <div className="text-sm text-ink/60 line-clamp-2">{a.breed.tagline}</div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-border pt-16">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start">
                            <div className="md:col-span-5">
                                <SectionLabel label={t("quiz.followJourney")} className="mb-6" />
                                <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight mb-4">
                                    {t("quiz.emailTitle")}
                                </h2>
                                <p className="text-ink/70 leading-relaxed">{t("quiz.emailBody")}</p>
                            </div>
                            <div className="md:col-span-7">
                                {emailSaved ? (
                                    <div className="bg-white border border-border rounded-[12px] p-8 md:p-12 text-center" data-testid="quiz-email-saved">
                                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest text-paper mb-6"><Check size={20} /></div>
                                        <div className="font-serif text-2xl">{t("quiz.emailSuccess")}</div>
                                        <p className="text-ink/60 mt-2">{t("quiz.emailSuccessSub")}</p>
                                    </div>
                                ) : (
                                    <form onSubmit={saveEmail} className="bg-white border border-border rounded-[12px] p-8 md:p-12" data-testid="quiz-email-form">
                                        <label className="overlabel text-earth block mb-4">{t("quiz.emailLabel")}</label>
                                        <div className="flex flex-col sm:flex-row items-stretch gap-4">
                                            <input
                                                type="email"
                                                placeholder={t("quiz.emailPlaceholder")}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                data-testid="quiz-email-input"
                                                className="flex-1 bg-transparent border-b border-border py-3 text-lg font-serif placeholder:text-mist/60 focus:border-forest focus:outline-none"
                                            />
                                            <button type="submit" data-testid="quiz-email-submit" className="group inline-flex items-center justify-center gap-3 bg-ink text-paper px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-gold hover:text-ink transition-colors duration-500">
                                                <Mail size={14} strokeWidth={1.5} />
                                                {t("common.buttons.sendLetter")}
                                            </button>
                                        </div>
                                        <p className="text-xs text-mist mt-4">{t("quiz.emailFooter")}</p>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
